import { useMemo } from 'react'
import { useReminders } from '../../../hooks/use-reminders'
import type { ReminderItem } from '../../../hooks/reminder-mapping'
import { useMeetingFollowUps } from '../use-meeting-follow-ups'
import { isMeetingReminder, mergeMeetingActionItems, type MeetingActionItem } from './meeting-action-items'
import { useMeetingActions } from './use-meeting-actions'

/** Everything the meeting left the user to do: the actions planner v2 staged for it and the reminders it wrote. */
export function useMeetingActionItems(
  audioSessionId: string | undefined,
  /** The saved note, which the meeting's final planner run often files its actions under instead. */
  audioNoteId?: string,
): { items: Array<MeetingActionItem> } {
  const { actions } = useMeetingActions(audioSessionId, audioNoteId)
  const { items: reminders } = useReminders()
  // The same fetch Recap's For You reads, and for the same reason its own
  // comment gives: the gateway resolves a note's reminders under the note *or*
  // its live session, which the local list can only match when it happens to
  // carry that link. Reading it here is free — Recap has the query warm — and
  // it is why one-column showed nothing for meetings whose Recap had two.
  const followUps = useMeetingFollowUps(audioNoteId)

  const items = useMemo(() => {
    const linkedHere = reminders.filter((reminder) => isMeetingReminder(reminder, audioSessionId, audioNoteId))
    const seen = new Set(linkedHere.map((reminder) => reminder.id))
    // Not filtered on `isOpen`: Recap renders every follow-up it fetched and
    // only counts the open ones in its badge, so filtering here would be a
    // second way the two lists could disagree.
    const fromNote: Array<ReminderItem> = followUps.items
      .filter((item) => !seen.has(item.reminder.id))
      .map((item) => item.reminder)
    return mergeMeetingActionItems(actions, [...linkedHere, ...fromNote])
  }, [actions, reminders, followUps.items, audioSessionId, audioNoteId])

  return { items }
}
